import { useCallback } from "react";

import type { GuestPageVisibilityMode, S3Object } from "@/lib/gql/__generated__/graphql";
import {
	PlainWidgetType,
	type PlainFontFamily,
	type PlainReservationComponentType,
	type PlainSizeType,
	type PlainWidgetDecoration,
} from "@/lib/gql/__generated__/graphql";
import { getUUID } from "@/lib/uuid/get-uuid";

import type * as FormModelType from "@/components/features/design/plain/model/type";

import { resolveVisibility } from "@/components/features-new/guest-page-visibility";

type Props = {
	viewModel: FormModelType.PlainSettingFormModel["ViewModel"];
	onSetViewModel: (viewModel: FormModelType.PlainSettingFormModel["ViewModel"]) => void;
	/** 保存済みのプレーンの公開状態（チェックを外したときに戻す値） */
	savedGuestPageVisibility: GuestPageVisibilityMode;
};

/**
 * ViewModel を更新する関数を定義している。
 * UI は export される関数をインターフェースとして利用する。
 */
export const useActions = ({ viewModel, onSetViewModel, savedGuestPageVisibility }: Props) => {
	/**
	 * ドメイン、サブドメイン名の変更
	 */
	const onChangeDomain = useCallback(
		(subDomainName: string) => {
			const baseDomain = import.meta.env.VITE_BASE_DOMAIN;
			const domainName = subDomainName + baseDomain;
			onSetViewModel({
				...viewModel,
				domainName,
				subDomainName: { ...viewModel.subDomainName, value: subDomainName },
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * 背景色の変更
	 */
	const onChangeColorBackground = useCallback(
		(value: string) => {
			if (!value) {
				onSetViewModel({
					...viewModel,
					colorBackground: { value: "#", error: undefined, isEdited: true },
				});
				return;
			}

			onSetViewModel({
				...viewModel,
				colorBackground: { value, error: undefined, isEdited: true },
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * FontColor の変更
	 */
	const onChangeColorFont = useCallback(
		(value: string) => {
			if (!value) {
				onSetViewModel({
					...viewModel,
					colorFont: { value: "#", error: undefined, isEdited: true },
				});
				return;
			}

			onSetViewModel({
				...viewModel,
				colorFont: { value, error: undefined, isEdited: true },
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * PrimaryColor の変更
	 */
	const onChangeColorPrimary = useCallback(
		(value: string) => {
			if (!value) {
				onSetViewModel({
					...viewModel,
					colorPrimary: { value: "#", error: undefined, isEdited: true },
				});
				return;
			}

			onSetViewModel({
				...viewModel,
				colorPrimary: { value, error: undefined, isEdited: true },
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * おすすめの配色を適用
	 */
	const onApplyRecommendationColors = useCallback(
		(colorBackground: string, colorPrimary: string, colorFont: string) => {
			onSetViewModel({
				...viewModel,
				colorBackground: { value: colorBackground, error: undefined, isEdited: true },
				colorPrimary: { value: colorPrimary, error: undefined, isEdited: true },
				colorFont: { value: colorFont, error: undefined, isEdited: true },
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * ロゴの変更
	 */
	const onChangeLogo = useCallback(
		(value: S3Object) => {
			onSetViewModel({
				...viewModel,
				logoImage: value
					? {
							bucket: {
								value: value.bucket,
								isEdited: true,
							},
							key: {
								value: value.key,
								isEdited: true,
							},
							region: {
								value: value.region,
								isEdited: true,
							},
							mimeType: {
								value: value.mimeType,
								isEdited: true,
							},
							fileName: {
								value: value.fileName,
								isEdited: true,
							},
						}
					: undefined,
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * ロゴのサイズの変更
	 */
	const onChangeLogoSize = useCallback(
		(value: PlainSizeType) => {
			onSetViewModel({ ...viewModel, logoSize: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * ホテル名の表示設定の変更
	 */
	const onChangeIsShowHotelName = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isShowHotelName: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * ロゴの表示設定の変更
	 */
	const onChangeIsShowHotelLogo = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isShowHotelLogo: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * キャッチコピーの表示設定の変更
	 */
	const onChangeIsShowHotelCatchCopy = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isShowHotelCatchCopy: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * キービジュアルの表示設定の変更
	 */
	const onChangeIsShowHotelKeyVisual = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isShowHotelKeyVisual: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * フォントの種類の変更
	 */
	const onChangeFontFamily = useCallback(
		(value: PlainFontFamily) => {
			onSetViewModel({ ...viewModel, fontFamily: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * ReservationComponent の種類の変更
	 */
	const onChangeReservationComponentType = useCallback(
		(value: PlainReservationComponentType) => {
			onSetViewModel({ ...viewModel, reservationComponentType: value });
		},
		[onSetViewModel, viewModel],
	);

	/**
	 * スナックの枠の種類の変更
	 */
	const onChangeWidgetDecoration = useCallback(
		(value: PlainWidgetDecoration) => {
			onSetViewModel({ ...viewModel, widgetDecoration: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * スナックの追加
	 */
	const onAddPlainWidgetDefault = useCallback(
		(contentID: string, title: string) => {
			onSetViewModel({
				...viewModel,
				plainWidgetDefault: {
					...viewModel.plainWidgetDefault,
					widgetItems: viewModel.plainWidgetDefault.widgetItems.concat({
						contentID,
						title,
						widgetID: getUUID(),
						widgetType: PlainWidgetType.Snack,
					}),
				},
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * スナックの削除
	 */
	const onRemovePlainWidgetDefault = useCallback(
		(widgetID: string) => {
			onSetViewModel({
				...viewModel,
				plainWidgetDefault: {
					...viewModel.plainWidgetDefault,
					widgetItems: viewModel.plainWidgetDefault.widgetItems.filter((item) => item.widgetID !== widgetID),
				},
			});
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * スナックの並び替え
	 */
	const onUpdateOrderPlainWidgets = useCallback(
		(widgetIDs: Array<string>) => {
			const widgetItems = widgetIDs
				.map((widgetID) => viewModel.plainWidgetDefault.widgetItems.find((item) => item.widgetID === widgetID))
				.filter((item) => item !== undefined);

			onSetViewModel({
				...viewModel,
				plainWidgetDefault: {
					...viewModel.plainWidgetDefault,
					widgetItems,
				},
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * マガジン/ホテル記事の表示設定の変更
	 */
	const onChangeIsShowMagazineAbout = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isShowMagazineAbout: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * マガジン/プラン記事の表示設定の変更
	 */
	const onChangeIsShowMagazinePlan = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isShowMagazinePlan: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * マガジン/部屋記事の表示設定の変更
	 */
	const onChangeIsShowMagazineRoom = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isShowMagazineRoom: value });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * GTM ID の変更
	 */
	const onChangeGtmID = useCallback(
		(value: string) => {
			onSetViewModel({ ...viewModel, gtmID: { value: value.length > 0 ? value : undefined, isEdited: true } });
		},
		[viewModel, onSetViewModel],
	);

	/**
	 * 検索エンジンへの表示の変更
	 * チェックありは検索に表示しない。外したときは保存済みの値に戻す（未設定を公開で上書きしない）
	 */
	const onChangeGuestPageUnlisted = useCallback(
		(isUnlisted: boolean) => {
			onSetViewModel({
				...viewModel,
				guestPageVisibility: resolveVisibility(isUnlisted, savedGuestPageVisibility),
			});
		},
		[viewModel, onSetViewModel, savedGuestPageVisibility],
	);

	return {
		// URL の設定
		onChangeDomain,

		// 色の設定
		onChangeColorBackground,
		onChangeColorFont,
		onChangeColorPrimary,
		onApplyRecommendationColors,

		// ホテルの表示設定
		onChangeLogo,
		onChangeLogoSize,
		onChangeIsShowHotelName,
		onChangeIsShowHotelLogo,
		onChangeIsShowHotelCatchCopy,
		onChangeIsShowHotelKeyVisual,

		// フォント設定
		onChangeFontFamily,

		// ReservationComponent の設定
		onChangeReservationComponentType,

		// スナックの設定
		onAddPlainWidgetDefault,
		onRemovePlainWidgetDefault,
		onChangeWidgetDecoration,
		onUpdateOrderPlainWidgets,
		// マガジン記事の表示設定
		onChangeIsShowMagazineAbout,
		onChangeIsShowMagazinePlan,
		onChangeIsShowMagazineRoom,

		// 分析
		onChangeGtmID,

		// 検索エンジンへの表示
		onChangeGuestPageUnlisted,
	};
};
